import React, { useCallback, useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Bold as BoldIcon, Italic as ItalicIcon, Heading2, Heading3, List, ListOrdered,
  Link as LinkIcon, Image as ImageIcon, Upload, Undo2, Redo2, Quote,
} from "lucide-react";

/**
 * Rich text editor built on TipTap.
 * Emits HTML through `onChange(html)` and expects `value` as HTML.
 * Toolbar: Bold, Italic, H2, H3, UL, OL, Blockquote, Link, Image URL, Image upload.
 */
export default function RichTextEditor({ value, onChange, testId = "rich-editor" }) {
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: "underline" } }),
      Image.configure({ inline: false, allowBase64: false }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "prose prose-neutral max-w-none min-h-[380px] px-5 py-4 focus:outline-none",
        "data-testid": `${testId}-content`,
      },
    },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Keep editor in sync when the outer value is reset (e.g. loading a different article)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current) editor.commands.setContent(value || "", { emitUpdate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, value]);

  const insertLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const href = window.prompt("URL link (lasă gol pentru a șterge):", prev);
    if (href === null) return;
    if (href === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
    }
  }, [editor]);

  const insertImageUrl = useCallback(() => {
    if (!editor) return;
    const src = window.prompt("URL imagine:");
    if (!src) return;
    editor.chain().focus().setImage({ src }).run();
  }, [editor]);

  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Fișierul trebuie să fie o imagine.");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/admin/media", form, { headers: { "Content-Type": "multipart/form-data" } });
      const src = data.url || data.path || data.src;
      if (!src) throw new Error("Server nu a returnat URL-ul.");
      editor?.chain().focus().setImage({ src }).run();
      toast.success("Imagine adăugată");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Upload eșuat");
    }
  };

  if (!editor) return null;

  const btn = (active) =>
    "font-mono-label border border-black w-8 h-8 flex items-center justify-center transition-colors " +
    (active ? "bg-black text-white" : "hover:bg-black hover:text-white");

  return (
    <div className="border border-black bg-white" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1 border-b border-black p-2 sticky top-0 bg-white z-10">
        <button type="button" data-testid={`${testId}-bold`} title="Bold" onClick={() => editor.chain().focus().toggleBold().run()} className={btn(editor.isActive("bold"))}>
          <BoldIcon size={14} />
        </button>
        <button type="button" data-testid={`${testId}-italic`} title="Italic" onClick={() => editor.chain().focus().toggleItalic().run()} className={btn(editor.isActive("italic"))}>
          <ItalicIcon size={14} />
        </button>
        <span className="w-px h-6 bg-black/20 mx-1" />
        <button type="button" data-testid={`${testId}-h2`} title="Titlu H2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={btn(editor.isActive("heading", { level: 2 }))}>
          <Heading2 size={14} />
        </button>
        <button type="button" data-testid={`${testId}-h3`} title="Titlu H3" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={btn(editor.isActive("heading", { level: 3 }))}>
          <Heading3 size={14} />
        </button>
        <span className="w-px h-6 bg-black/20 mx-1" />
        <button type="button" data-testid={`${testId}-ul`} title="Listă cu buline" onClick={() => editor.chain().focus().toggleBulletList().run()} className={btn(editor.isActive("bulletList"))}>
          <List size={14} />
        </button>
        <button type="button" data-testid={`${testId}-ol`} title="Listă numerotată" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btn(editor.isActive("orderedList"))}>
          <ListOrdered size={14} />
        </button>
        <button type="button" data-testid={`${testId}-quote`} title="Citat" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={btn(editor.isActive("blockquote"))}>
          <Quote size={14} />
        </button>
        <span className="w-px h-6 bg-black/20 mx-1" />
        <button type="button" data-testid={`${testId}-link`} title="Link" onClick={insertLink} className={btn(editor.isActive("link"))}>
          <LinkIcon size={14} />
        </button>
        <button type="button" data-testid={`${testId}-image-url`} title="Imagine din URL" onClick={insertImageUrl} className={btn(false)}>
          <ImageIcon size={14} />
        </button>
        <button
          type="button"
          data-testid={`${testId}-image-upload`}
          title="Încarcă imagine din calculator"
          onClick={() => fileInputRef.current?.click()}
          className={btn(false)}
        >
          <Upload size={14} />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid={`${testId}-file-input`}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) { uploadImage(f); e.target.value = ""; } }}
        />
        <span className="w-px h-6 bg-black/20 mx-1" />
        <button type="button" title="Undo" onClick={() => editor.chain().focus().undo().run()} className={btn(false)}>
          <Undo2 size={14} />
        </button>
        <button type="button" title="Redo" onClick={() => editor.chain().focus().redo().run()} className={btn(false)}>
          <Redo2 size={14} />
        </button>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
